import * as THREE from "three";
import Experience from "../Experience.js";

export default class HeroCrystals {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.camera = this.experience.camera;
    this.resources = this.experience.resources;
    this.raycaster = this.experience.raycaster;

    this.initializeConstants();

    this.time = 0;
    this.hoveredCrystal = null;
    this.isLoadingComplete = false; // Track loading state

    this.crystalGroups = [];

    // Reusable objects to avoid garbage collection during animation loops
    this.tempMatrix = new THREE.Matrix4();
    this.rotationMatrix = new THREE.Matrix4();
    this.tempEuler = new THREE.Euler();

    this.createHeroCrystals();

    // Listen for loading completion event
    this.experience.on("loadingComplete", () => {
      this.isLoadingComplete = true;
    });
  }

  initializeConstants() {
    this.ROTATION_SPEED = 0.0011;

    this.BOB_SPEED = 0.1;
    this.HOVER_HYSTERESIS_FRAMES = 3;
    this.BOB_ANIMATION_THRESHOLD = 0.05;
    this.BASE_BOB_DURATION = 1000;
    this.LERP_FACTOR = 0.083;

    // Configuration for all crystal formations with positions, scales, and layer definitions
    this.CRYSTAL_CONFIGS = {
      main: {
        position: { x: 18, y: 4, z: -8 },
        scale: { x: 1.1, y: 1.1, z: 1.1 },
        userData: {
          type: "main",
          scale: 1.0,
          bobHeightMultiplier: 0.8,
          parallaxYMultiplier: 0.055,
        },
        layers: [
          {
            geometry: "dodecahedron",
            color: 0xaa00ff,
            opacity: 0.3,
            scale: { x: 5, y: 5, z: 5 },
          },
          {
            geometry: "icosahedron",
            color: 0xffc800,
            opacity: 0.24,
            scale: { x: 4, y: 4, z: 4 },
          },
          {
            geometry: "octahedron",
            color: 0x0000ff,
            opacity: 0.18,
            scale: { x: 3, y: 3, z: 3 },
          },
          {
            geometry: "box",
            color: 0x00ff00,
            opacity: 0.12,
            scale: { x: 2, y: 2, z: 2 },
          },
          {
            geometry: "tetrahedron",
            color: 0xff0000,
            opacity: 0.06,
            scale: { x: 1, y: 1, z: 1 },
          },
        ],
      },
      rightMain: {
        position: { x: 28, y: 1, z: -8 },
        scale: { x: 0.8, y: 0.8, z: 0.8 },
        userData: {
          type: "rightMain",
          scale: 0.8,
          bobHeightMultiplier: 0.8,
          parallaxYMultiplier: 0.06,
        },
        layers: [
          {
            geometry: "icosahedron",
            color: 0x0000ff,
            opacity: 0.24,
            scale: { x: 4, y: 4, z: 4 },
          },
          {
            geometry: "octahedron",
            color: 0xff0000,
            opacity: 0.18,
            scale: { x: 3, y: 3, z: 3 },
          },
          {
            geometry: "box",
            color: 0xffc800,
            opacity: 0.12,
            scale: { x: 2, y: 2, z: 2 },
          },
          {
            geometry: "tetrahedron",
            color: 0x00ff00,
            opacity: 0.06,
            scale: { x: 1, y: 1, z: 1 },
          },
        ],
      },
      leftMain: {
        position: { x: 11, y: 6, z: -2 },
        scale: { x: 0.5, y: 0.5, z: 0.5 },
        userData: {
          type: "leftMain",
          scale: 0.5,
          bobHeightMultiplier: 0.4,
          parallaxYMultiplier: 0.035,
        },
        layers: [
          {
            geometry: "icosahedron",
            color: 0xff0000,
            opacity: 0.24,
            scale: { x: 4, y: 4, z: 4 },
          },
          {
            geometry: "octahedron",
            color: 0x00ff00,
            opacity: 0.18,
            scale: { x: 3, y: 3, z: 3 },
          },
          {
            geometry: "box",
            color: 0x0000ff,
            opacity: 0.12,
            scale: { x: 2, y: 2, z: 2 },
          },
          {
            geometry: "tetrahedron",
            color: 0xffc800,
            opacity: 0.06,
            scale: { x: 1, y: 1, z: 1 },
          },
        ],
      },
      bottomLeft: {
        position: { x: 7, y: 0, z: -18 },
        scale: { x: 0.5, y: 0.5, z: 0.5 },
        userData: {
          type: "bottomLeft",
          scale: 0.5,
          bobHeightMultiplier: 0.8,
          parallaxYMultiplier: 0.1,
        },
        layers: [
          {
            geometry: "icosahedron",
            color: 0x00ffaa,
            opacity: 0.25,
            scale: { x: 3, y: 3, z: 3 },
          },
          {
            geometry: "octahedron",
            color: 0xaa00ff,
            opacity: 0.35,
            scale: { x: 2, y: 2, z: 2 },
          },
        ],
      },
      topRight: {
        position: { x: 28, y: 7, z: -16 },
        scale: { x: 0.4, y: 0.4, z: 0.4 },
        userData: {
          type: "topRight",
          scale: 0.4,
          bobHeightMultiplier: 0.8,
          parallaxYMultiplier: 0.045,
        },
        layers: [
          {
            geometry: "icosahedron",
            color: 0xff6600,
            opacity: 0.25,
            scale: { x: 3, y: 3, z: 3 },
          },
          {
            geometry: "octahedron",
            color: 0x0066ff,
            opacity: 0.35,
            scale: { x: 2, y: 2, z: 2 },
          },
        ],
      },
    };
  }

  createHeroCrystals() {
    const envMap = this.resources.items.crystalTexture;
    envMap.mapping = THREE.EquirectangularReflectionMapping;
    envMap.colorSpace = THREE.SRGBColorSpace;

    Object.keys(this.CRYSTAL_CONFIGS).forEach((crystalType) => {
      this.createCrystalFormation(this.CRYSTAL_CONFIGS[crystalType], envMap);
    });
  }

  createCrystalFormation(config, envMap) {
    const crystalGroup = new THREE.Group();
    crystalGroup.position.set(
      config.position.x,
      config.position.y,
      config.position.z
    );
    crystalGroup.scale.set(config.scale.x, config.scale.y, config.scale.z);

    config.layers.forEach((layer) => {
      this.createCrystalMesh(
        crystalGroup,
        layer.geometry,
        layer.color,
        layer.opacity,
        layer.scale,
        envMap,
        10
      );
    });

    crystalGroup.userData = this.createCrystalUserData(config.userData);
    crystalGroup.userData.originalPosition = crystalGroup.position.clone();

    this.scene.add(crystalGroup);
    this.crystalGroups.push(crystalGroup);
  }

  createCrystalUserData(configUserData) {
    // Use config-defined parallax multipliers
    const parallaxMultiplier = {
      x: 0, // No X movement - Y-only parallax
      y: window.innerHeight * configUserData.parallaxYMultiplier,
    };

    return {
      type: configUserData.type,
      originalPosition: new THREE.Vector3(),
      parallaxOffset: { x: 0, y: 0, z: 0 },
      targetParallaxOffset: { x: 0, y: 0, z: 0 },
      parallaxMultiplier: parallaxMultiplier,
      bobOffset: 0,
      targetBobOffset: 0,
      isHovered: false,
      hoverFrameCount: 0,
      bobStartTime: 0,
      bobDuration: 0,
      shouldBobDown: false,
      configUserData: configUserData, // Store reference to config for custom values
    };
  }

  setParallaxOffset(progress) {
    this.crystalGroups.forEach((group) => {
      group.userData.targetParallaxOffset = {
        x: progress * group.userData.parallaxMultiplier.x,
        y: progress * group.userData.parallaxMultiplier.y,
        z: 0,
      };
    });
  }

  createCrystalMesh(
    parentGroup,
    geometryType,
    color,
    opacity,
    scale,
    envMap,
    instanceCount = 1
  ) {
    let geometry;
    switch (geometryType) {
      case "dodecahedron":
        geometry = new THREE.DodecahedronGeometry();
        break;
      case "icosahedron":
        geometry = new THREE.IcosahedronGeometry();
        break;
      case "octahedron":
        geometry = new THREE.OctahedronGeometry();
        break;
      case "box":
        geometry = new THREE.BoxGeometry();
        break;
      case "tetrahedron":
        geometry = new THREE.TetrahedronGeometry();
        break;
      default:
        geometry = new THREE.DodecahedronGeometry();
    }

    const material = new THREE.MeshPhysicalMaterial({
      color: color,
      wireframe: false,
      envMap: envMap,
      side: THREE.BackSide,
      roughness: 0,
      transmission: 0.6,
      transparent: true,
      thickness: 1.6,
      opacity: opacity,
      ior: 1.4,
      clearcoat: 0.1,
      clearcoatRoughness: 0.1,
    });

    let mesh;

    if (instanceCount === 1) {
      mesh = new THREE.Mesh(geometry, material);
      mesh.scale.set(scale.x, scale.y, scale.z);
    } else {
      mesh = new THREE.InstancedMesh(geometry, material, instanceCount);
      mesh.scale.set(scale.x, scale.y, scale.z);

      const dummy = new THREE.Object3D();
      const baseMatrices = [];

      for (let i = 0; i < instanceCount; i++) {
        dummy.scale.set(1 + i * 0.01, 1 + i * 0.01, 1 + i * 0.01);
        dummy.position.set(0, 0, 0);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();

        baseMatrices.push(dummy.matrix.clone());
        mesh.setMatrixAt(i, dummy.matrix);
      }

      mesh.userData.baseMatrices = baseMatrices;
    }

    mesh.renderOrder = -1;
    mesh.userData.geometryType = geometryType;
    mesh.userData.groupIndex = parentGroup.children.length;

    parentGroup.add(mesh);
    return mesh;
  }

  update() {
    this.crystalGroups.forEach((group) => {
      const userData = group.userData;

      userData.parallaxOffset.x = THREE.MathUtils.lerp(
        userData.parallaxOffset.x,
        userData.targetParallaxOffset.x,
        this.LERP_FACTOR
      );
      userData.parallaxOffset.y = THREE.MathUtils.lerp(
        userData.parallaxOffset.y,
        userData.targetParallaxOffset.y,
        this.LERP_FACTOR
      );
      userData.parallaxOffset.z = THREE.MathUtils.lerp(
        userData.parallaxOffset.z,
        userData.targetParallaxOffset.z,
        this.LERP_FACTOR
      );

      userData.bobOffset = THREE.MathUtils.lerp(
        userData.bobOffset,
        userData.targetBobOffset,
        this.BOB_SPEED
      );

      group.position.copy(userData.originalPosition);
      group.position.x += userData.parallaxOffset.x;
      group.position.y += userData.parallaxOffset.y + userData.bobOffset;
      group.position.z += userData.parallaxOffset.z;
    });

    this.hoveredCrystal = null;
    const currentTime = Date.now();

    this.crystalGroups.forEach((group) => {
      const userData = group.userData;

      const isBobAnimating =
        Math.abs(userData.bobOffset - userData.targetBobOffset) >
        this.BOB_ANIMATION_THRESHOLD;

      if (!isBobAnimating && this.isLoadingComplete) {
        const groupMeshes = group.children;
        const isGroupHovered = this.raycaster.isHovering(groupMeshes, true);

        if (isGroupHovered) {
          this.hoveredCrystal = group;
          userData.hoverFrameCount++;
        } else {
          userData.hoverFrameCount = 0;
        }

        if (
          !userData.isHovered &&
          userData.hoverFrameCount >= this.HOVER_HYSTERESIS_FRAMES
        ) {
          userData.isHovered = true;
          userData.targetBobOffset =
            userData.configUserData.bobHeightMultiplier *
            (1.0 / userData.configUserData.scale);
          userData.bobStartTime = currentTime;
          userData.bobDuration =
            this.BASE_BOB_DURATION * userData.configUserData.scale;
          userData.shouldBobDown = false;
        }
      }

      // Check if crystal should bob down based on timer (regardless of hover state)
      if (userData.isHovered && !userData.shouldBobDown) {
        const timeElapsed = currentTime - userData.bobStartTime;
        if (timeElapsed >= userData.bobDuration) {
          userData.shouldBobDown = true;
        }
      }

      // Bob down when timer expires
      if (userData.shouldBobDown && userData.targetBobOffset > 0) {
        userData.isHovered = false;
        userData.targetBobOffset = 0;
        userData.shouldBobDown = false;
      }
    });

    this.time += this.ROTATION_SPEED;

    this.crystalGroups.forEach((group, groupIdx) => {
      const rotationSpeed =
        group.userData.type === "main" ? 0.3 : 0.5 + groupIdx * 0.1;

      // Add directional variation - some crystals rotate in opposite directions
      const directionX = groupIdx % 2 === 0 ? 1 : -1; // Alternate X direction
      const directionY = groupIdx % 3 === 0 ? 1 : -1; // Different pattern for Y direction

      group.rotation.y += this.ROTATION_SPEED * rotationSpeed * directionY;
      group.rotation.x += this.ROTATION_SPEED * 0.2 * directionX;

      group.children.forEach((mesh) => {
        if (mesh.isInstancedMesh) {
          const instanceCount = mesh.count;
          const meshIndex = mesh.userData.groupIndex;
          const baseMatrices = mesh.userData.baseMatrices;

          // Add directional variation for individual meshes within groups
          const meshDirectionX = (meshIndex + groupIdx) % 2 === 0 ? 1 : -1;
          const meshDirectionY = (meshIndex + groupIdx + 1) % 2 === 0 ? 1 : -1;
          const meshDirectionZ = meshIndex % 2 === 0 ? 1 : -1;

          const rotX = this.time * meshDirectionX + meshIndex * 0.1;
          const rotY = this.time * 0.7 * meshDirectionY + meshIndex * 0.15;
          const rotZ = this.time * 0.5 * meshDirectionZ + meshIndex * 0.2;

          this.tempEuler.set(rotX, rotY, rotZ);
          this.rotationMatrix.makeRotationFromEuler(this.tempEuler);

          for (let i = 0; i < instanceCount; i++) {
            this.tempMatrix.copy(baseMatrices[i]);
            this.tempMatrix.multiply(this.rotationMatrix);
            mesh.setMatrixAt(i, this.tempMatrix);
          }

          mesh.instanceMatrix.needsUpdate = true;
        } else {
          // Add directional variation for non-instanced meshes
          const meshDirectionX = (groupIdx + 1) % 2 === 0 ? 1 : -1;
          const meshDirectionY = (groupIdx + 2) % 2 === 0 ? 1 : -1;
          const meshDirectionZ = groupIdx % 2 === 0 ? 1 : -1;

          mesh.rotation.x +=
            this.ROTATION_SPEED * (2 + groupIdx * 0.5) * meshDirectionX;
          mesh.rotation.y +=
            this.ROTATION_SPEED * (1.5 + groupIdx * 0.3) * meshDirectionY;
          mesh.rotation.z +=
            this.ROTATION_SPEED * (1 + groupIdx * 0.2) * meshDirectionZ;
        }
      });
    });
  }

  dispose() {
    this.crystalGroups.forEach((group) => {
      group.children.forEach((mesh) => {
        mesh.geometry.dispose();
        mesh.material.dispose();
      });
      this.scene.remove(group);
    });

    this.crystalGroups = [];
  }
}
